import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  HostListener,
  inject,
  INJECTOR,
  OnDestroy,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MessageBarService, modalFn, translator } from '@softline/ui-core';
import {
  addOnHost,
  BackNavigable,
  BackNavigationService,
  block,
  BlockingLoadingSpinnerComponent,
  handleRequestError,
  MengenEingabeComponent,
  menuItems,
  requestFn,
  Scan,
  ScannerModule,
  ScannerStore,
  showRequestErrors,
  SOFTLINE_FEATURE_SCANNER,
  SOFTLINE_FEATURE_TITLE,
  TitleStore,
} from '@softline/application';
import {
  asapScheduler,
  BehaviorSubject,
  combineLatestWith,
  filter,
  firstValueFrom,
  map,
  Observable,
  observeOn,
  skip,
  Subscription,
  switchMap,
  take,
} from 'rxjs';
import { ApiDataAccess, ResponseResult, Store } from '@softline/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  SOFTLINE_CONFIG_VOR_KOMMISSIONIERUNG,
  SOFTLINE_FEATURE_KOMMISSIONIERUNG,
} from '../../kommissionierung.shared';
import { KommissionierungStore } from '../../store';
import {
  KommissionsauftragBewe,
  KommissionsauftragKopf,
} from '../../types/kommissionsauftrag';
import { offeneKopfkommsMenuItemFn } from '../../functions/offene-kopfkomms-menu-item.function';
import {
  Lagerplatz,
  LagerplatzInhalt,
  LagerplatzSucheComponent,
  LagerplatzSucheStore,
  Palette,
  SOFTLINE_FEATURE_LAGERPLATZ_SUCHE,
} from '@softapps/lager/core';
import { KommmissionierungStrategy } from '../../strategies/kommmissionierung.strategy';
import { kommissionierungStrategyFactory } from '../../strategies/kommissionierung.strategy-factory';
import { toSignal } from '@angular/core/rxjs-interop';
import { PageComponent, PageHeaderComponent } from '@softapps/core/pages';
import { StoreItemComponent } from '../../components/store-item/store-item.component';
import { PickingOrderComponent } from '../../components/picking-order/picking-order.component';
import { PickingPalletComponent } from '../../components/picking-pallet/picking-pallet.component';
import { AdditionalItemQuantityModalComponent } from '../../dialogs/additional-item-quantity-modal/additional-item-quantity-modal.component';
import { KommissionierungService } from '../../services/kommissionierung.service';
import { KommissionierungAddOn } from '@softapps/lager/kommissionierung/api';

import { CheckboxComponent } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
import { MischpaletteStrategy } from '../../strategies/mischpalette.strategy';
import { CancellationModalComponent } from '../../dialogs/cancellation-modal/cancellation-modal.component';
import { QuantityInputModal } from '../../dialogs/quantity-input-modal/quantity-input-modal';
import { EinheitenUmbuchungModalComponent } from '../../dialogs/einheiten-umbuchung-modal/einheiten-umbuchung-modal.component';
import { unitEntryModalFn } from '@softapps/wws/core';
@Component({
  selector: 'soft-artikel-menge-page',
  imports: [
    CommonModule,
    ScannerModule,
    MengenEingabeComponent,
    BlockingLoadingSpinnerComponent,
    PageComponent,
    PageHeaderComponent,
    StoreItemComponent,
    LagerplatzSucheComponent,
    PickingOrderComponent,
    PickingPalletComponent,
    CheckboxComponent,
    TranslatePipe,
  ],
  templateUrl: './artikel-menge.page.html',
  styleUrls: ['./artikel-menge.page.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ArtikelMengePage implements OnInit, OnDestroy, BackNavigable {
  readonly config = inject(SOFTLINE_CONFIG_VOR_KOMMISSIONIERUNG, { optional: true });
  readonly service = inject(KommissionierungService);
  readonly modal = modalFn();
  private stockTransferOpen = false;
  readonly #injector = inject(INJECTOR);
  readonly #apiDataAccess = inject(ApiDataAccess);

  menge: number = 0;

  private request = requestFn();
  private t = translator();

  private auftragSubscription?: Subscription;
  private beweSubscription?: Subscription;
  private scanSubscription?: Subscription;
  strategy$ = new BehaviorSubject<KommmissionierungStrategy | undefined>(undefined);
  loading$ = new BehaviorSubject<boolean>(false);
  disabled$ = new BehaviorSubject<boolean>(false);

  readonly messageBarService = inject(MessageBarService);

  readonly selectedPalette = signal<Palette | null>(null);

  auftrag$: Observable<KommissionsauftragKopf> = this.route.paramMap.pipe(
    map((o) => +(o.get('idkopfkomm') ?? 0)),
    switchMap((o) =>
      this.store.observe(
        SOFTLINE_FEATURE_KOMMISSIONIERUNG,
        KommissionierungStore.getters.entity,
        o
      )
    ),
    observeOn(asapScheduler)
  );

  bewe$: Observable<KommissionsauftragBewe | undefined> = this.route.paramMap.pipe(
    map((o) => +(o.get('idbewekomm') ?? 0)),
    combineLatestWith(this.auftrag$),
    map(([idbewekomm, auftrag]) => auftrag?.bewegungen?.find((o) => o.id === idbewekomm)),
    observeOn(asapScheduler)
  );

  lagerplatz$: Observable<LagerplatzInhalt> = this.route.paramMap.pipe(
    map((o) => ({
      idkopfkomm: +(o.get('idkopfkomm') ?? 0),
      idbewekomm: +(o.get('idbewekomm') ?? 0),
    })),
    switchMap((o) =>
      this.store.observe(
        SOFTLINE_FEATURE_KOMMISSIONIERUNG,
        KommissionierungStore.getters.lagerplatzvorschlag,
        o
      )
    )
  );
  menge$: Observable<number> = this.route.paramMap.pipe(
    map((o) => ({
      idkopfkomm: +(o.get('idkopfkomm') ?? 0),
      idbewekomm: +(o.get('idbewekomm') ?? 0),
    })),
    switchMap((o) =>
      this.store.observe(
        SOFTLINE_FEATURE_KOMMISSIONIERUNG,
        KommissionierungStore.getters.menge,
        o
      )
    )
  );
  maxMenge$ = this.strategy$.pipe(
    combineLatestWith(this.lagerplatz$, this.bewe$),
    map(([strategy, lagerplatz, bewe]) => {
      if (!strategy) return 0;
      return strategy.getMaxMenge(lagerplatz, bewe?.artikelMengeOffen);
    })
  );

  protected inventoryHeaderTemplate = viewChild.required('inventoryHeaderTemplate', {
    read: TemplateRef,
  });

  readonly bewe = toSignal(this.bewe$);
  readonly auftrag = toSignal(this.auftrag$);
  private readonly offeneKopfkommsMenuItem = offeneKopfkommsMenuItemFn();
  readonly strategy = toSignal(this.strategy$);

  private readonly disabled = toSignal(this.disabled$);
  private readonly loading = toSignal(this.loading$);
  private readonly _menge = toSignal(this.menge$);
  private readonly lagerplatz = toSignal(this.lagerplatz$);

  readonly isValid = computed(() => {
    return (
      this.disabled() ||
      this.loading() ||
      (this._menge() ?? 0) <= 0 ||
      !this.lagerplatz()?.lagerplatz
    );
  });

  constructor(
    private store: Store,
    private router: Router,
    private route: ActivatedRoute,
    private backNavigationService: BackNavigationService,
    private cdRef: ChangeDetectorRef,
    public vcRef: ViewContainerRef
  ) {
    menuItems(() => {
      return [
        {
          name: 'Zusätzliche Menge erfassen',
          title: 'Zusätzliche Menge erfassen',
          icon: 'fa-regular fa-square-plus',
          outlet: 'action',
          tooltip: true,
          tooltipPosition: 'left',
          type: 'execute',
          canExecute: true,
          visible: this.auftrag()?.mischpalette !== true,
          execute: async () => {
            const auftrag = this.auftrag();
            if (!auftrag || !auftrag?.id) return;

            // TODO
            const manuelEntryAllowed = !!auftrag;

            if (!manuelEntryAllowed) {
              this.messageBarService.warning(
                'Manuelle Erfassung/Änderung ist bei Barverkauf und Kassabon nicht erlaubt',
                'Aktion nicht erlaubt'
              );
              return;
            }

            const quantityResult = await this.modal({
              title: 'Zusätzliche Menge erfassen',
              subtitle:
                'Bitte geben Sie die Menge für die neue Kommissionierungsbewegung an',
              separateContentSection: true,
              component: AdditionalItemQuantityModalComponent,
              class: 'max-h-[90vh]!',
              data: {
                id: this.bewe()?.artikelMengeOffen?.artikel.id,
                quantity: 1,
              },
            });

            if (!quantityResult || quantityResult === 'DISMISSED') {
              return;
            }

            this.messageBarService.success(
              'Die neue Bewegung scheint in der Artikelübersicht auf',
              'Erstellen erfolgreich!'
            );

            try {
              const bewes = await block(
                () => this.service.createAdditionalItem(auftrag.id, quantityResult),
                this.#injector
              );

              for (const bewe of bewes) {
                this.store.commit(
                  SOFTLINE_FEATURE_KOMMISSIONIERUNG,
                  KommissionierungStore.mutations.patch,
                  {
                    id: auftrag.id,
                    changes: {
                      bewegungen: [...(auftrag?.bewegungen ?? []), bewe],
                    },
                  }
                );
              }
            } catch (e) {
              handleRequestError(e, this.#injector, { show: true });
            }
          },
        },
        {
          name: 'einheiten-umbuchen',
          title: this.t('#KOMMISSIONIERUNG.ACTIONS.EINHEITEN_UMBUCHEN')(),
          icon: 'fa-regular fa-right-left',
          outlet: 'action',
          tooltip: true,
          tooltipPosition: 'left',
          type: 'execute',
          canExecute: true,
          visible: this.bewe()?.artikelMengeOffen?.artikel?.kzartikelart === 'DIF',
          execute: async () => {
            const auftrag = this.auftrag();
            const artikelMenge = this.bewe()?.artikelMengeOffen;
            if (!auftrag || !artikelMenge) return;

            this.stockTransferOpen = true;
            try {
              await this.modal({
                title: this.t('#KOMMISSIONIERUNG.ACTIONS.EINHEITEN_UMBUCHEN')(),
                separateContentSection: true,
                component: EinheitenUmbuchungModalComponent,
                class: 'max-h-[90vh]!',
                data: { artikelMenge, idlagerstamm: auftrag.lager?.id },
              });
            } finally {
              this.stockTransferOpen = false;
            }
          },
        },
        this.offeneKopfkommsMenuItem(this.auftrag()),
      ];
    });

    addOnHost(KommissionierungAddOn, {
      lagerplatz: this.lagerplatz,
      bewe: this.bewe,
      auftrag: this.auftrag,
      disabled$: this.disabled$,
      loadLagerplatzVorschlag: async (bewe: any) => this.loadLagerplatzVorschlag(bewe),
    });
  }

  async ngOnInit(): Promise<void> {
    this.backNavigationService.set(this);
    this.store.commit(
      SOFTLINE_FEATURE_TITLE,
      TitleStore.mutations.setTitle,
      '#KOMMISSIONIERUNG.TITLE'
    );
    this.auftragSubscription = this.auftrag$.subscribe((o) => {
      if (o) this.strategy$.next(kommissionierungStrategyFactory(o));
      this.store.commit(SOFTLINE_FEATURE_TITLE, TitleStore.mutations.setSubTitle, {
        subTitle: '#KOMMISSIONIERUNG.SUBTITLE',
        params: o,
      });
      this.cdRef.detectChanges();
    });
    this.beweSubscription = this.bewe$
      .pipe(
        combineLatestWith(
          this.strategy$.pipe(
            filter((o) => !!o),
            take(1)
          )
        ),
        filter(([bewe]) => bewe?.artikelMengeOffen?.menge !== 0)
      )
      .subscribe(async ([bewe, strategy]) => {
        if (!bewe || !strategy?.loadLagerplatzVorschlag) return;
        await this.loadLagerplatzVorschlag(bewe);
      });

    this.scanSubscription = this.store
      .observe(SOFTLINE_FEATURE_SCANNER, ScannerStore.getters.latest)
      .pipe(skip(1))
      .subscribe(async (scan) => {
        // The Umbuchen dialog scans its own Ziel-Lagerplatz
        if (!scan || this.stockTransferOpen) return;
        await this.loadLagerplatz(scan);
      });
  }

  async ngOnDestroy(): Promise<void> {
    if (this.auftragSubscription && !this.auftragSubscription.closed)
      this.auftragSubscription.unsubscribe();
    this.auftragSubscription = undefined;
    if (this.scanSubscription && !this.scanSubscription.closed)
      this.scanSubscription.unsubscribe();
    this.scanSubscription = undefined;
    if (this.beweSubscription && !this.beweSubscription.closed)
      this.beweSubscription.unsubscribe();
    this.beweSubscription = undefined;

    this.store.commit(SOFTLINE_FEATURE_TITLE, TitleStore.mutations.setTitle, '');
    this.store.commit(SOFTLINE_FEATURE_TITLE, TitleStore.mutations.setSubTitle, {
      subTitle: '',
      params: undefined,
    });

    this.backNavigationService.set(undefined);
  }

  async navigateBack(): Promise<void> {
    const auftrag = await firstValueFrom(this.auftrag$);
    const bewes = auftrag?.bewegungen?.filter((o) => o.artikelMengeOffen.menge > 0) ?? [];
    if (bewes.length >= 1)
      await this.router.navigate(['./../../', auftrag.id], { relativeTo: this.route });
    else await this.router.navigate(['./../../'], { relativeTo: this.route });
  }

  @HostListener('window:keydown.arrowright', ['$event'])
  async next(_: Event): Promise<void> {
    if (this.disabled$.value) return;

    const bewe = await firstValueFrom(this.bewe$);
    const auftrag = await firstValueFrom(this.auftrag$);
    if (!bewe || !auftrag) return;
    let index = auftrag?.bewegungen?.indexOf(bewe) ?? 0;
    if (index >= (auftrag?.bewegungen?.length ?? 0) - 1) index = 0;
    else index++;
    const idbewekomm = (auftrag?.bewegungen ?? [])[index]?.id;
    await this.router.navigate(['./../../', auftrag.id, idbewekomm], {
      relativeTo: this.route,
    });
  }

  @HostListener('window:keydown.arrowleft', ['$event'])
  async previous(_: Event): Promise<void> {
    if (this.disabled$.value) return;

    const bewe = await firstValueFrom(this.bewe$);
    const auftrag = await firstValueFrom(this.auftrag$);
    if (!bewe || !auftrag) return;
    let index = auftrag?.bewegungen?.indexOf(bewe) ?? 0;
    if (index === 0) index = (auftrag?.bewegungen?.length ?? 0) - 1;
    else index--;
    const idbewekomm = (auftrag?.bewegungen ?? [])[index]?.id;
    await this.router.navigate(['./../../', auftrag.id, idbewekomm], {
      relativeTo: this.route,
    });
  }

  @HostListener('window:keydown.enter')
  async onSubmit(): Promise<void> {
    if (this.disabled$.value) return;

    const idkopfkomm = +(this.route.snapshot.paramMap.get('idkopfkomm') ?? 0);
    const idbewekomm = +(this.route.snapshot.paramMap.get('idbewekomm') ?? 0);

    if (this.loading$.getValue()) return;

    this.loading$.next(true);
    try {
      const resultOrKopfkommResult = await this.store.dispatch(
        SOFTLINE_FEATURE_KOMMISSIONIERUNG,
        KommissionierungStore.actions.artikelKommissionierung,
        { idkopfkomm, idbewekomm, scanPalette: this.selectedPalette() ?? undefined }
      );

      console.log(resultOrKopfkommResult);

      if ('kopfkomm' in resultOrKopfkommResult) {
        const stockQuantity = resultOrKopfkommResult.lagerplatzInhalt?.inhalt?.find(
          (o) =>
            o.artikel.id === resultOrKopfkommResult.bewekommscan.artikelMenge.artikel.id
        )?.menge;

        // TODO
        if (
          stockQuantity === 0 ||
          (resultOrKopfkommResult?.lagerplatzInhalt?.inhalt &&
            resultOrKopfkommResult.lagerplatzInhalt.inhalt.length === 0)
        ) {
          this.loading$.next(false);

          const result = await this.modal({
            component: QuantityInputModal,
            separateContentSection: true,
            title: 'Inventur: 0 - Menge bestätigen',
            subtitle: 'Bitte prüfen, ob wirklich kein Artikel mehr am Lagerplatz ist!',
            headerTemplate: this.inventoryHeaderTemplate,
            data: {
              bewekomm: resultOrKopfkommResult.bewekommscan,
            },
          });

          if (result && result !== 'DISMISSED' && 'kopfkomm' in resultOrKopfkommResult) {
            await this.request({
              request: async () => {
                const inventoryResult = (
                  await this.#apiDataAccess.create<
                    any,
                    ResponseResult<{
                      lagerplatzInhalt: LagerplatzInhalt;
                      idbeweinvs: number[];
                    }>
                  >(
                    { path: '/v1/lagerplatz/inventur' },
                    {
                      lagerplatz: resultOrKopfkommResult.lagerplatzInhalt?.lagerplatz,
                      inhalt: [
                        {
                          ...resultOrKopfkommResult.bewekommscan.artikelMenge,
                          arteheingabe:
                            resultOrKopfkommResult.bewekommscan.artikelMenge.arteheingabe,
                          arteheingabePflicht:
                            resultOrKopfkommResult.bewekommscan.artikelMenge
                              .arteheingabePflicht,
                          artikel:
                            resultOrKopfkommResult.bewekommscan.artikelMenge.artikel,
                          einheit:
                            resultOrKopfkommResult.bewekommscan.artikelMenge.einheit,
                          einheit_lg:
                            resultOrKopfkommResult.bewekommscan.artikelMenge.einheit_lg ??
                            resultOrKopfkommResult.bewekommscan.artikelMenge.einheit,
                          einheitenAuswahl:
                            resultOrKopfkommResult.bewekommscan.artikelMenge
                              .einheitenAuswahl,
                          idarteheingabe:
                            resultOrKopfkommResult.bewekommscan.artikelMenge
                              .idarteheingabe,

                          menge_lg:
                            resultOrKopfkommResult.bewekommscan.artikelMenge.menge_lg,
                          paletteartId: resultOrKopfkommResult.bewekommscan.paletteartId,
                          vermerk:
                            resultOrKopfkommResult.bewekommscan.artikelMenge.vermerk,
                          menge_inv: result.artikelMengeOffen.menge,
                          menge: result.artikelMengeOffen.menge,
                        },
                      ],
                    }
                  )
                )?.result;

                if (
                  inventoryResult?.idbeweinvs &&
                  inventoryResult.idbeweinvs.length > 0
                ) {
                  await this.#apiDataAccess.patch(
                    {
                      path: '/v1/bewekommscan/{{idbewekomm}}',
                      pathParams: {
                        idbewekomm: resultOrKopfkommResult.bewekommscan.id,
                      },
                    },
                    {
                      idbeweinv: inventoryResult.idbeweinvs[0],
                    }
                  );
                }
              },
              options: {
                type: 'blocking',
              },
            });
          }
        }
      }

      const result =
        'kopfkomm' in resultOrKopfkommResult
          ? resultOrKopfkommResult.kopfkomm
          : resultOrKopfkommResult;

      const bewe = result?.bewegungen?.find((o) => o.id === idbewekomm);
      if (!bewe) throw new Error('[KommissionierungStoreExtension]: No Bewe found');

      const strategy = this.strategy$.value;
      if (!strategy) return;

      this.store.commit(
        SOFTLINE_FEATURE_KOMMISSIONIERUNG,
        KommissionierungStore.mutations.removeLagerplatzvorschlag,
        { idkopfkomm, idbewekomm }
      );

      let lagerplatz: LagerplatzInhalt | undefined;

      if (strategy?.loadLagerplatzVorschlag && bewe.artikelMengeOffen.menge > 0) {
        lagerplatz = await this.store.dispatch(
          SOFTLINE_FEATURE_KOMMISSIONIERUNG,
          KommissionierungStore.actions.loadLagerplatzVorschlag,
          { idkopfkomm, idbewekomm }
        );
      }

      const menge = strategy.getMaxMenge(lagerplatz, bewe.artikelMengeOffen);
      this.store.commit(
        SOFTLINE_FEATURE_KOMMISSIONIERUNG,
        KommissionierungStore.mutations.setMenge,
        { idkopfkomm, idbewekomm, menge }
      );

      this.loading$.next(false);

      const hasOpenBeweOrMischpalette =
        !!result?.bewegungen?.find((o) => o.artikelMengeOffen.menge > 0) ||
        (result?.mischpaletten?.length || 0) > 0;

      if (hasOpenBeweOrMischpalette)
        await this.router.navigate(['./../../', result.id], { relativeTo: this.route });
      else {
        await this.router.navigate(['./../../', result.id, 'abschluss'], {
          relativeTo: this.route,
        });
      }
    } catch (e) {
      this.loading$.next(false);
      showRequestErrors(this.store, e);
    } finally {
      this.loading$.next(false);
    }
  }

  async onScan(): Promise<void> {
    await this.store.dispatch(SOFTLINE_FEATURE_SCANNER, ScannerStore.actions.scan, {});
  }

  async loadLagerplatzVorschlag(bewe: KommissionsauftragBewe): Promise<void> {
    this.loading$.next(true);

    const idkopfkomm = +(this.route.snapshot.paramMap.get('idkopfkomm') ?? 0);
    try {
      const strategy = this.strategy$.value;
      if (!strategy || bewe.artikelMengeOffen.menge === 0) return;

      const result = await this.store.dispatch(
        SOFTLINE_FEATURE_KOMMISSIONIERUNG,
        KommissionierungStore.actions.loadLagerplatzVorschlag,
        { idkopfkomm, idbewekomm: bewe.id }
      );

      const menge = strategy.getMaxMenge(result, bewe.artikelMengeOffen);
      this.store.commit(
        SOFTLINE_FEATURE_KOMMISSIONIERUNG,
        KommissionierungStore.mutations.setMenge,
        { idkopfkomm, idbewekomm: bewe.id, menge }
      );
      this.loading$.next(false);
    } catch (e) {
      this.loading$.next(false);
      showRequestErrors(this.store, e);
    } finally {
      this.loading$.next(false);
    }
  }

  async loadLagerplatz(scan: Scan): Promise<void> {
    const idkopfkomm = +(this.route.snapshot.paramMap.get('idkopfkomm') ?? 0);
    const idbewekomm = +(this.route.snapshot.paramMap.get('idbewekomm') ?? 0);
    this.loading$.next(true);

    const bewe = this.bewe();

    if ((bewe?.offenePaletten?.length || 0) > 0) {
      const foundPalette = bewe?.offenePaletten?.find((bewe) => bewe.sscc === scan.data);

      if (foundPalette) {
        this.selectedPalette.set(foundPalette);
      } else {
        this.messageBarService.error('Es wurde eine nicht richtige Palette gescanned!');
        this.selectedPalette.set(null);
        this.loading$.next(false);
        return;
      }
    }

    try {
      this.loading$.next(false);
      const lagerplatz: Lagerplatz | undefined = await this.store.dispatch(
        SOFTLINE_FEATURE_LAGERPLATZ_SUCHE,
        LagerplatzSucheStore.actions.suche,
        scan.data
      );
      const strategy = this.strategy$.value;
      if (!lagerplatz || !strategy) return;
      this.loading$.next(true);
      const result = await this.store.dispatch(
        SOFTLINE_FEATURE_KOMMISSIONIERUNG,
        KommissionierungStore.actions.loadLagerplatz,
        { idkopfkomm, idbewekomm, code: lagerplatz.barcode }
      );
      this.loading$.next(false);

      const bewe = await firstValueFrom(this.bewe$);
      const menge = strategy.getMaxMenge(result, bewe?.artikelMengeOffen);
      this.store.commit(
        SOFTLINE_FEATURE_KOMMISSIONIERUNG,
        KommissionierungStore.mutations.setMenge,
        { idkopfkomm, idbewekomm, menge }
      );
    } catch (e) {
      this.loading$.next(false);
      showRequestErrors(this.store, e);
    } finally {
      this.loading$.next(false);
    }
  }

  onMengeChange(idkopfkomm: number, idbewekomm: number, menge: number): void {
    this.store.commit(
      SOFTLINE_FEATURE_KOMMISSIONIERUNG,
      KommissionierungStore.mutations.setMenge,
      { idkopfkomm, idbewekomm, menge }
    );
  }

  async onLpChange(lagerplatz: Lagerplatz): Promise<void> {
    await this.onLagerplatzChange({
      lagerplatz,
      inhalt: [],
      palette: undefined,
      mischpaletten: [],
    });
  }

  async onLagerplatzChange(lagerplatzInhalt: LagerplatzInhalt): Promise<void> {
    const bewe = await firstValueFrom(this.bewe$);
    const menge = this.strategy()?.getMaxMenge(lagerplatzInhalt, bewe?.artikelMengeOffen);
    const idkopfkomm = +(this.route.snapshot.paramMap.get('idkopfkomm') ?? 0);
    const idbewekomm = +(this.route.snapshot.paramMap.get('idbewekomm') ?? 0);
    this.store.commit(
      SOFTLINE_FEATURE_KOMMISSIONIERUNG,
      KommissionierungStore.mutations.setMenge,
      { idkopfkomm, idbewekomm, menge }
    );

    this.store.commit(
      SOFTLINE_FEATURE_KOMMISSIONIERUNG,
      KommissionierungStore.mutations.setLagerplatzvorschlag,
      { idkopfkomm, idbewekomm, lagerplatzvorschlag: lagerplatzInhalt }
    );
  }

  selectPalette(palette: Palette) {
    this.selectedPalette.set(palette);
  }

  private readonly unitEntryModal = unitEntryModalFn();

  async openMengeDialog(idkopfkomm: number, bewegung: KommissionsauftragBewe) {
    const result = await this.unitEntryModal({
      params: {
        idartstamm: bewegung.artikelMengeOffen.artikel.id,
        menge: !bewegung?.artikelMengeOffen
          ? bewegung.artikelMengeOffen.menge || 0
          : undefined,
        idbasisEinheit: bewegung.artikelMengeOffen.einheit.id,
        idarteheingabe: bewegung?.artikelMengeOffen.idarteheingabe,
      },
    });

    if (
      result &&
      typeof result !== 'string' &&
      bewegung.artikelMengeOffen.einheit.id !== result.summeEinheit.einheit.id
    )
      return;

    if (!result || result === 'DISMISSED') return;

    this.onMengeChange(idkopfkomm, bewegung.id, result.erfassungsEinheit.menge ?? 0)
  }
}
